// ---------------------------------------------------------------------------
// HRIS · Staff Attendance — public check-in page (scan destination)
// ---------------------------------------------------------------------------
// This is where a staff member lands after scanning the attendance QR. It is a
// PUBLIC page (registered outside the authenticated layouts in src/index.js), so
// it wraps its own ChakraProvider and reads all school context from the URL that
// the QR encoded — never from localStorage / auth.
// ---------------------------------------------------------------------------
import React, {useMemo, useState} from "react";
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    ChakraProvider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Heading,
    Icon,
    Input,
    Select,
    Stack,
    Text,
    Textarea,
} from "@chakra-ui/react";
import {CheckCircleIcon} from "@chakra-ui/icons";
import {MdHowToReg, MdLogin, MdLogout} from "react-icons/md";
import theme from "theme/theme.js";

import {
    ATTENDANCE_ACTION,
    ATTENDANCE_ACTION_META,
    defaultAttendanceAction,
} from "../../constants/HrisConstants";
import {staffCheckIn, staffCheckOut} from "../../services/HrisAttendanceService";
import {kathmanduISOString, parseCheckinParams} from "../../utils/attendanceQr";

const prettyDate = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {weekday: "long", year: "numeric", month: "long", day: "numeric"});
};

function CheckinInner() {
    // react-router v5 HashRouter puts params after the hash; the QR encodes them
    // as part of the hash path, so read straight from window.location.hash.
    const ctx = useMemo(() => {
        const hash = window.location.hash || "";
        const qIndex = hash.indexOf("?");
        const search = qIndex >= 0 ? hash.slice(qIndex) : "";
        return parseCheckinParams(search);
    }, []);

    const invalidQr = !ctx.schoolCode && !ctx.schoolName && !ctx.schoolId;

    const [form, setForm] = useState({employeeCode: "", fullName: "", remarks: ""});
    // Morning (before 12:00) defaults to Check In, afternoon/evening to Check Out.
    const [action, setAction] = useState(() => defaultAttendanceAction());
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null); // { at, action }
    const [failed, setFailed] = useState("");

    const isCheckIn = action === ATTENDANCE_ACTION.CHECK_IN;

    const handleChange = (e) => {
        const {name, value} = e.target;
        setForm((prev) => ({...prev, [name]: value}));
        setErrors((prev) => ({...prev, [name]: undefined}));
    };

    const validate = () => {
        const next = {};
        if (!form.employeeCode.trim()) next.employeeCode = "Employee code is required";
        if (!form.fullName.trim()) next.fullName = "Full name is required";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        setFailed("");
        // Punch time in Nepal time (Asia/Kathmandu, +05:45), independent of the
        // scanning device's own timezone.
        const at = kathmanduISOString();
        const basePayload = {
            schoolCode: ctx.schoolCode || null,
            schoolId: ctx.schoolId || null,
            session: ctx.session || null,
            date: ctx.date || null,
            employeeCode: form.employeeCode.trim(),
            fullName: form.fullName.trim(),
            remarks: form.remarks.trim() || null,
        };
        try {
            if (isCheckIn) {
                await staffCheckIn({...basePayload, checkInAt: at});
            } else {
                await staffCheckOut({...basePayload, checkOutAt: at});
            }
            setDone({at, action});
        } catch (error) {
            setFailed(
                error?.response?.data?.message ||
                error?.message ||
                "Could not record your attendance. Please try again or contact the office."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const bg = "gray.50";

    return (
        <Flex minH="100vh" bg={bg} align="center" justify="center" p="16px">
            <Box w="100%" maxW="420px" bg="white" borderRadius="20px" boxShadow="lg" p="24px">
                {invalidQr ? (
                    <Alert status="error" borderRadius="12px">
                        <AlertIcon/>
                        This QR is invalid or has expired. Please scan the latest attendance QR.
                    </Alert>
                ) : done ? (
                    <Flex direction="column" align="center" textAlign="center" gap="12px" py="16px">
                        <Icon as={CheckCircleIcon} w="56px" h="56px" color="green.400"/>
                        <Heading size="md" color="gray.700">
                            {ATTENDANCE_ACTION_META[done.action].label} recorded
                        </Heading>
                        <Text color="gray.500">
                            Thank you, <b>{form.fullName.trim()}</b>. You {ATTENDANCE_ACTION_META[done.action].verb} for{" "}
                            {prettyDate(ctx.date)}.
                        </Text>
                        <Badge colorScheme="green" borderRadius="6px" px="10px" py="4px">
                            {new Date(done.at).toLocaleTimeString("en-US", {hour: "numeric", minute: "2-digit"})}
                        </Badge>
                    </Flex>
                ) : (
                    <Stack spacing="18px">
                        <Flex direction="column" align="center" textAlign="center" gap="4px">
                            <Icon as={MdHowToReg} w="40px" h="40px" color="teal.400"/>
                            <Heading size="md" color="gray.700">Mark Attendance</Heading>
                            <Text color="gray.500" fontSize="sm">
                                {ctx.schoolName || "Your School"}
                                {ctx.schoolCode ? ` · ${ctx.schoolCode}` : ""}
                            </Text>
                            <Text color="gray.400" fontSize="xs">{prettyDate(ctx.date)}</Text>
                        </Flex>

                        {failed && (
                            <Alert status="error" borderRadius="10px" fontSize="sm">
                                <AlertIcon/>
                                {failed}
                            </Alert>
                        )}

                        <FormControl>
                            <FormLabel>Action</FormLabel>
                            <Select
                                value={action}
                                onChange={(e) => setAction(e.target.value)}
                                icon={isCheckIn ? <MdLogin/> : <MdLogout/>}
                            >
                                <option value={ATTENDANCE_ACTION.CHECK_IN}>
                                    {ATTENDANCE_ACTION_META.CHECK_IN.label}
                                </option>
                                <option value={ATTENDANCE_ACTION.CHECK_OUT}>
                                    {ATTENDANCE_ACTION_META.CHECK_OUT.label}
                                </option>
                            </Select>
                            <FormHelperText fontSize="xs">
                                Defaulted to <b>{ATTENDANCE_ACTION_META[action].label}</b> based on the current time.
                            </FormHelperText>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!errors.employeeCode}>
                            <FormLabel>Employee Code</FormLabel>
                            <Input
                                name="employeeCode"
                                value={form.employeeCode}
                                onChange={handleChange}
                                placeholder="e.g. EMP-001"
                                autoComplete="off"
                            />
                            <FormErrorMessage>{errors.employeeCode}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!errors.fullName}>
                            <FormLabel>Full Name</FormLabel>
                            <Input
                                name="fullName"
                                value={form.fullName}
                                onChange={handleChange}
                                placeholder="Your full name"
                            />
                            <FormErrorMessage>{errors.fullName}</FormErrorMessage>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Remarks (optional)</FormLabel>
                            <Textarea
                                name="remarks"
                                value={form.remarks}
                                onChange={handleChange}
                                placeholder="Anything the office should know"
                                rows={2}
                            />
                        </FormControl>

                        <Button
                            colorScheme="teal"
                            size="lg"
                            leftIcon={isCheckIn ? <MdLogin/> : <MdLogout/>}
                            onClick={handleSubmit}
                            isLoading={submitting}
                            loadingText="Submitting"
                        >
                            {ATTENDANCE_ACTION_META[action].label}
                        </Button>
                    </Stack>
                )}
            </Box>
        </Flex>
    );
}

// Public entry — brings its own Chakra theme since it renders outside the app
// layouts.
export default function StaffAttendanceCheckin() {
    return (
        <ChakraProvider theme={theme} resetCss={false}>
            <CheckinInner/>
        </ChakraProvider>
    );
}
